// Import react elements
import { useEffect, useRef, useState } from "react";

// Import css stylesheet
import "./LineChart.css";



const defaultSeriesColours = [
    "var(--primary-color)",
    "#28a745",
    "#e63946",
    "#ff7f00",
    "#6a0dad",
    "#ff1493"
];



const LineChart = ({
    data = [],
    xKey,
    series = [],
    axes = [],
    formatXValue = (value) => value,
    height = 340
}) => {

    // Store the chart container so the SVG can match its available width
    const containerRef = useRef(null);

    // Store the current rendered chart width
    const [chartWidth, setChartWidth] = useState(900);

    // Store which data series are currently visible
    const [visibleSeries, setVisibleSeries] = useState(() => series.map((seriesItem) => seriesItem.dataKey));

    // Store the point currently being hovered
    const [hoverIndex, setHoverIndex] = useState(null);



    // Keep the chart width in sync with its container
    useEffect(() => {

        // Stop if the chart container is not available yet
        if (!containerRef.current) { return; }

        // Update the stored width from the current container size
        const updateChartWidth = () => {

            const nextWidth = containerRef.current?.getBoundingClientRect().width || 0;
            if (nextWidth > 0) { setChartWidth(nextWidth); }

        };

        updateChartWidth();

        // Watch the container for responsive size changes
        const resizeObserver = new ResizeObserver(() => {

            updateChartWidth();

        });

        resizeObserver.observe(containerRef.current);

        // Stop watching when the component is removed
        return () => resizeObserver.disconnect();

    }, []);



    // Toggle one series without changing the others
    const toggleSeries = (dataKey) => {

        // Remove the series when it is currently visible
        if (visibleSeries.includes(dataKey)) {

            setVisibleSeries(visibleSeries.filter((visibleDataKey) => visibleDataKey !== dataKey));
            return;

        }

        // Add the series when it is currently hidden
        setVisibleSeries([...visibleSeries, dataKey]);

    };



    // Return the configured axis for a series
    const getSeriesAxis = (seriesItem) => {

        // Use the requested axis when it exists
        const matchedAxis = axes.find((axis) => axis.id === seriesItem.axisId);
        if (matchedAxis) { return matchedAxis; }

        // Fall back to a basic left axis
        return {
            id: seriesItem.axisId || "default",
            position: "left",
            formatValue: (value) => Number(value).toLocaleString("en-GB")
        };

    };



    // Calculate the visible value range for one Y axis
    const getAxisRange = (axis) => {

        const axisValues = [];

        // Collect values from every visible series using this axis
        series.forEach((seriesItem) => {

            // Skip hidden series
            if (!visibleSeries.includes(seriesItem.dataKey)) { return; }

            // Skip series assigned to another axis
            if (getSeriesAxis(seriesItem).id !== axis.id) { return; }

            // Collect numeric values from every row
            data.forEach((row) => {

                const value = Number(row?.[seriesItem.dataKey]);
                if (Number.isFinite(value)) { axisValues.push(value); }

            });

        });

        // Use a simple zero-to-one range when the axis has no values
        if (axisValues.length === 0) { return { min: 0, max: 1 }; }

        let min = Math.min(0, ...axisValues);
        let max = Math.max(0, ...axisValues);

        // Keep integer count axes on clean whole-number steps
        if (axis.integer === true && min >= 0) {

            const integerMax = Math.max(1, Math.ceil(max));
            const integerStep = Math.max(1, Math.ceil(integerMax / 4));

            return {
                min: 0,
                max: integerStep * 4
            };

        }

        // Give an all-zero axis a usable scale
        if (min === 0 && max === 0) { return { min: 0, max: 1 }; }

        // Give the plotted lines a little space above and below their values
        const range = max - min || Math.abs(max) || 1;
        if (max > 0) { max = max + (range * 0.08); }
        if (min < 0) { min = min - (range * 0.08); }

        return { min, max };

    };



    // Calculate each configured axis range once for this render
    const axisRanges = {};
    axes.forEach((axis) => {

        axisRanges[axis.id] = getAxisRange(axis);

    });



    const leftMargin = 72;
    const rightMargin = 64;
    const topMargin = 18;
    const bottomMargin = 46;
    const plotWidth = Math.max(1, chartWidth - leftMargin - rightMargin);
    const plotHeight = Math.max(1, height - topMargin - bottomMargin);



    // Convert a row index into an X coordinate
    const getX = (index) => {

        // Centre a single point in the chart
        if (data.length <= 1) { return leftMargin + (plotWidth / 2); }

        return leftMargin + ((index / (data.length - 1)) * plotWidth);

    };



    // Convert a value into a Y coordinate using its configured axis
    const getY = (value, axis) => {

        const range = axisRanges[axis.id] || { min: 0, max: 1 };
        const rangeSize = range.max - range.min || 1;
        const valueRatio = (Number(value) - range.min) / rangeSize;

        return topMargin + plotHeight - (valueRatio * plotHeight);

    };



    // Build a line path for one data series
    const buildSeriesPath = (seriesItem) => {

        const axis = getSeriesAxis(seriesItem);
        const pathParts = [];

        // Add each row to the SVG path in X-axis order
        data.forEach((row, index) => {

            const value = Number(row?.[seriesItem.dataKey]);
            if (!Number.isFinite(value)) { return; }

            const x = getX(index);
            const y = getY(value, axis);

            pathParts.push(`${pathParts.length === 0 ? "M" : "L"} ${x} ${y}`);

        });

        return pathParts.join(" ");

    };



    // Choose a small number of evenly spread X-axis labels
    const getXTickIndexes = () => {

        // Return every row when there are only a few points
        if (data.length <= 7) { return data.map((row, index) => index); }

        const tickIndexes = [];

        // Build seven labels including the first and last rows
        for (let tick = 0; tick <= 6; tick += 1) {

            const index = Math.round((tick / 6) * (data.length - 1));
            if (!tickIndexes.includes(index)) { tickIndexes.push(index); }

        }

        return tickIndexes;

    };



    // Update the hover point from the mouse position over the SVG
    const handleMouseMove = (event) => {

        // Stop if there is no data to inspect
        if (data.length === 0) { return; }

        const bounds = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - bounds.left;
        const clampedX = Math.max(leftMargin, Math.min(leftMargin + plotWidth, mouseX));
        const xRatio = plotWidth > 0 ? (clampedX - leftMargin) / plotWidth : 0;
        const nextIndex = data.length <= 1 ? 0 : Math.round(xRatio * (data.length - 1));

        setHoverIndex(nextIndex);

    };



    // Return the display value for one series point
    const formatSeriesValue = (seriesItem, value) => {

        const axis = getSeriesAxis(seriesItem);

        // Use the axis formatter when one was supplied
        if (typeof axis.formatValue === "function") { return axis.formatValue(value); }

        return Number(value).toLocaleString("en-GB");

    };



    const xTickIndexes = getXTickIndexes();
    const activeAxes = axes.filter((axis) => series.some((seriesItem) => visibleSeries.includes(seriesItem.dataKey) && getSeriesAxis(seriesItem).id === axis.id));
    const hoveredRow = hoverIndex !== null ? data[hoverIndex] : null;



    return (

        <div className="line-chart" ref={containerRef}>

            <div className="line-chart__series-filters">

                {/* Build one visibility toggle for each supplied series */}
                {series.map((seriesItem, seriesIndex) => {

                    const seriesColour = seriesItem.color || defaultSeriesColours[seriesIndex % defaultSeriesColours.length];
                    const isVisible = visibleSeries.includes(seriesItem.dataKey);

                    return (

                        <button
                            key={seriesItem.dataKey}
                            type="button"
                            className={`line-chart__series-filter ${isVisible ? "line-chart__series-filter--active" : ""}`}
                            style={{ "--line-chart-series-colour": seriesColour }}
                            onClick={() => toggleSeries(seriesItem.dataKey)}
                        >

                            <span className="line-chart__series-filter-dot" />
                            {seriesItem.label}

                        </button>

                    );

                })}

            </div>

            <div className="line-chart__plot-wrap">

                {data.length === 0 ? (

                    <div className="line-chart__empty">No chart data available.</div>

                ) : (

                    <>

                        <svg
                            className="line-chart__svg"
                            width="100%"
                            height={height}
                            viewBox={`0 0 ${chartWidth} ${height}`}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={() => setHoverIndex(null)}
                        >

                            {/* Draw horizontal chart grid lines */}
                            {[0, 1, 2, 3, 4].map((tick) => {

                                const y = topMargin + ((tick / 4) * plotHeight);

                                return (
                                    <line
                                        key={`grid-${tick}`}
                                        className="line-chart__grid-line"
                                        x1={leftMargin}
                                        x2={leftMargin + plotWidth}
                                        y1={y}
                                        y2={y}
                                    />
                                );

                            })}

                            {/* Draw labels for each active Y axis */}
                            {activeAxes.map((axis) => {

                                const range = axisRanges[axis.id] || { min: 0, max: 1 };
                                const axisX = axis.position === "right" ? leftMargin + plotWidth + 10 : leftMargin - 10;
                                const textAnchor = axis.position === "right" ? "start" : "end";

                                return (

                                    <g key={axis.id}>

                                        {/* Draw five labels down this Y axis */}
                                        {[0, 1, 2, 3, 4].map((tick) => {

                                            const value = range.max - ((tick / 4) * (range.max - range.min));
                                            const y = topMargin + ((tick / 4) * plotHeight);

                                            return (
                                                <text
                                                    key={`${axis.id}-${tick}`}
                                                    className="line-chart__axis-label"
                                                    x={axisX}
                                                    y={y + 4}
                                                    textAnchor={textAnchor}
                                                >
                                                    {typeof axis.formatValue === "function" ? axis.formatValue(value) : Number(value).toLocaleString("en-GB")}
                                                </text>
                                            );

                                        })}

                                    </g>

                                );

                            })}

                            {/* Draw the selected X-axis labels */}
                            {xTickIndexes.map((index) => (

                                <text
                                    key={`x-${index}`}
                                    className="line-chart__axis-label line-chart__axis-label--x"
                                    x={getX(index)}
                                    y={height - 12}
                                    textAnchor="middle"
                                >
                                    {formatXValue(data[index]?.[xKey])}
                                </text>

                            ))}

                            {/* Draw each visible data series */}
                            {series.map((seriesItem, seriesIndex) => {

                                // Skip hidden series
                                if (!visibleSeries.includes(seriesItem.dataKey)) { return null; }

                                const seriesColour = seriesItem.color || defaultSeriesColours[seriesIndex % defaultSeriesColours.length];

                                return (
                                    <path
                                        key={seriesItem.dataKey}
                                        className="line-chart__series-line"
                                        d={buildSeriesPath(seriesItem)}
                                        style={{ stroke: seriesColour }}
                                    />
                                );

                            })}

                            {/* Draw the hover guide and selected series points */}
                            {hoveredRow && (

                                <g>

                                    <line
                                        className="line-chart__hover-line"
                                        x1={getX(hoverIndex)}
                                        x2={getX(hoverIndex)}
                                        y1={topMargin}
                                        y2={topMargin + plotHeight}
                                    />

                                    {/* Draw one hover point for each visible series */}
                                    {series.map((seriesItem, seriesIndex) => {

                                        // Skip hidden series
                                        if (!visibleSeries.includes(seriesItem.dataKey)) { return null; }

                                        const value = Number(hoveredRow?.[seriesItem.dataKey]);
                                        if (!Number.isFinite(value)) { return null; }

                                        const axis = getSeriesAxis(seriesItem);
                                        const seriesColour = seriesItem.color || defaultSeriesColours[seriesIndex % defaultSeriesColours.length];

                                        return (
                                            <circle
                                                key={`hover-${seriesItem.dataKey}`}
                                                className="line-chart__hover-point"
                                                cx={getX(hoverIndex)}
                                                cy={getY(value, axis)}
                                                r="4"
                                                style={{ fill: seriesColour }}
                                            />
                                        );

                                    })}

                                </g>

                            )}

                        </svg>

                        {hoveredRow && (

                            <div className="line-chart__tooltip">

                                <p className="line-chart__tooltip-title">
                                    {formatXValue(hoveredRow?.[xKey], true)}
                                </p>

                                {/* List the value for each visible series at this point */}
                                {series.map((seriesItem, seriesIndex) => {

                                    // Skip hidden series
                                    if (!visibleSeries.includes(seriesItem.dataKey)) { return null; }

                                    const seriesColour = seriesItem.color || defaultSeriesColours[seriesIndex % defaultSeriesColours.length];

                                    return (

                                        <div key={`tooltip-${seriesItem.dataKey}`} className="line-chart__tooltip-row">

                                            <span
                                                className="line-chart__tooltip-dot"
                                                style={{ backgroundColor: seriesColour }}
                                            />

                                            <span>{seriesItem.label}</span>
                                            <strong>{formatSeriesValue(seriesItem, hoveredRow?.[seriesItem.dataKey])}</strong>

                                        </div>

                                    );

                                })}

                            </div>

                        )}

                    </>

                )}

            </div>

        </div>

    );

};

export default LineChart;
